<script setup lang="ts">
import SmallModel from '@/components/Interface/Modal/ModelSizes/SmallModel.vue'
import { computed, reactive, ref } from 'vue'
import { useGameStore } from '@/stores/gameStore.ts'
import FormProcess from '@/models/formProcess.ts'
import FormGenericError from '@/components/Interface/Modal/Forms/FormGenericError.vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import { useShipActionStore } from '@/stores/shipActionStore.ts'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const gameStore = useGameStore()
const shipActionStore = useShipActionStore()
const formProcess = new FormProcess()

// Form state
const waitDuration = ref('5') // number as string

// Generate wait duration options from 1 to 100 days
const waitOptions = computed(() => {
  const options = []

  // Add options for 1-10 days individually
  for (let days = 1; days <= 10; days++) {
    options.push({
      value: days.toString(),
      label: t('modals.waitAction.waitFor', days)
    })
  }

  // Add options from 15 to 100 days in steps of 5
  for (let days = 15; days <= 100; days += 5) {
    options.push({
      value: days.toString(),
      label: t('modals.waitAction.waitFor', days)
    })
  }

  return options
})

// Calculate duration based on selection
const duration = computed(() => {
  return parseInt(waitDuration.value)
})

// Form errors
const formErrors = reactive<{ [key: string]: string }>({
  generic: ''
})

// Handle form submission
const submitWait = () => {
  formProcess.resetFormErrors(formErrors)

  if (duration.value < 1 || duration.value > 100) {
    formErrors.generic = t('modals.waitAction.error.invalidDuration')
    return
  }

  // Set pending action to wait
  shipActionStore.setPendingAction('wait')

  // Use the position where the last command ended if there are previous commands,
  // otherwise use the current position
  const x = shipActionStore.actions.length > 0 ? shipActionStore.pathEndX : shipActionStore.pathStartX
  const y = shipActionStore.actions.length > 0 ? shipActionStore.pathEndY : shipActionStore.pathStartY

  // Queue the wait action with duration
  shipActionStore.queuePendingAction(
    x,
    y,
    0,
    0,
    duration.value
  )

  gameStore.modalType = ''
}
</script>

<template>
  <SmallModel :title="t('modals.waitAction.title')">
    <div class="p-4">
      <div class="mb-4">
        <div class="text-subtext mb-3">
          {{ t('modals.waitAction.description') }}
        </div>

        <!-- Wait duration selector -->
        <div class="mb-4">
          <label class="block mb-2 text-slate-300">{{ t('modals.waitAction.duration') }}</label>
          <select
            v-model="waitDuration"
            class="w-full p-2 text-sm border rounded bg-slate-800 border-slate-600 text-white focus:ring-blue-500 focus:border-blue-500"
          >
            <option
              v-for="option in waitOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>

        <!-- Duration preview -->
        <div class="mb-4 p-3 bg-slate-800 rounded">
          <div class="text-sm text-slate-300">
            <strong>{{ t('modals.waitAction.durationLabel') }}</strong> {{ t('modals.waitAction.daysCount', duration) }}
          </div>
          <div class="text-xs text-subtext mt-1">
            {{ t('modals.waitAction.waitDescription', { days: duration }) }}
          </div>
        </div>
      </div>

      <div class="flex justify-between pt-2">
        <FormGenericError :error="formErrors.generic" />
        <div class="flex gap-2">
          <ActionButton @click="gameStore.modalType = ''" class="bg-slate-600 hover:bg-slate-500">
            {{ t('modals.common.cancel') }}
          </ActionButton>
          <ActionButton @click="submitWait">
            {{ t('modals.waitAction.queueWait') }}
          </ActionButton>
        </div>
      </div>
    </div>
  </SmallModel>
</template>

<style scoped lang="scss">
select {
  @apply bg-slate-800 border border-slate-600 rounded focus:ring-blue-500 focus:border-blue-500;
}
</style>